import { useRef, useState } from "react";
import { AlertTriangle, Check, Copy } from "lucide-react";

import { buildAskClaudePrompt, type AskClaudePromptInput } from "@/domains/trade-ideas/lib/askClaudePrompt";
import { Button } from "@/shared/ui/button";

/**
 * JS-1083: "Ask Claude" handoff -- copies a facts-only prompt (ADR-011, see
 * `askClaudePrompt.ts`) naming this idea's `ledger_id` to the clipboard, so
 * the visitor can paste it into any MCP-connected AI client and have Claude
 * call the new `trade_ideas_get_by_ledger_id` MCP tool. Shared by both
 * Desk-idea surfaces (`MultiStrategyScreenTable`'s thesis drawer and
 * `EditionPage`'s idea cards) so the copy affordance/behavior never diverges
 * between them (NO-TECH-DEBT, mirroring `deskIdeaWatchPrefill.ts`'s and
 * `tickerContextLink.ts`'s own shared-helper precedent).
 *
 * Copy-to-clipboard convention lifted from `ConnectorTab.tsx`'s `CopyButton`
 * (`copyValue`, the 1.6s "Copied!" affordance) -- v1 is copy/paste only, no
 * in-app chat and no OS deep link.
 *
 * Disabled honestly (never hidden) when the row has no `ledgerId` -- the
 * ticket's own "disabled honestly when the row has no ledger_id" requirement,
 * matching the sibling "+Watch" / "Open ticker view" buttons' convention.
 *
 * JS-1087 (fix 1): `disabledReason` lets a caller force this same
 * disabled-with-honest-tooltip state for a reason OTHER than a missing
 * ledger_id -- specifically, an idea whose direction is unrecognized (see
 * deskIdeaWatchPrefill.hasKnownDirection). The prompt this button copies
 * names the idea's direction as a published fact (askClaudePrompt.ts's
 * `Direction: ...` clause), so handing a visitor a copyable prompt for an
 * idea with no verified direction would let that fact silently go missing
 * from an otherwise-normal-looking action -- fail closed instead, exactly
 * like the sibling "+Watch" button does for the same input.
 *

 * FIX 4 (adversarial review): a clipboard write can fail two distinct ways --
 * `navigator.clipboard` can be entirely absent (older/non-secure-context
 * browsers, some embedded webviews) or `writeText` can reject (permission
 * denied, focus lost, etc.). Both must render a VISIBLE, accessible failure
 * state distinct from idle -- silently reverting to idle (the pre-fix
 * behavior) leaves the visitor believing nothing happened, when in fact the
 * prompt never reached their clipboard. On failure this renders a
 * `role="alert"` "Copy failed" note plus a read-only, auto-selected textarea
 * holding the exact prompt text so the visitor can still copy it manually.
 */
export function AskClaudeButton({
  ledgerId,
  promptInput,
  className,
  disabledReason,
}: {
  ledgerId: string | null | undefined;
  promptInput: Omit<AskClaudePromptInput, "ledgerId">;
  className?: string;
  /** JS-1087: when present, forces the disabled state with THIS honest
   * reason instead of the default "no ledger reference yet" copy -- used
   * for an idea whose direction is unrecognized. Checked ahead of the
   * ledgerId gate below since an unknown direction is the more fundamental
   * reason (mirrors the same ordering `deskIdeaWatchAvailability`'s callers
   * apply for "+Watch"). */
  disabledReason?: string | null;
}) {
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");
  const [fallbackPrompt, setFallbackPrompt] = useState<string | null>(null);
  const fallbackRef = useRef<HTMLTextAreaElement | null>(null);
  const disabled = Boolean(disabledReason) || !ledgerId;

  const copyPrompt = async () => {
    if (disabled || !ledgerId) return;
    const prompt = buildAskClaudePrompt({ ledgerId, ...promptInput });
    const clipboard = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
    if (!clipboard) {
      setStatus("failed");
      setFallbackPrompt(prompt);
      return;
    }
    try {
      await clipboard.writeText(prompt);
      setStatus("copied");
      setFallbackPrompt(null);
      window.setTimeout(() => setStatus((current) => (current === "copied" ? "idle" : current)), 1600);
    } catch {
      setStatus("failed");
      setFallbackPrompt(prompt);
    }
  };

  return (
    <div className={className ?? "w-full"}>
      <Button
        size="sm"
        variant="outline"
        className="w-full"
        disabled={disabled}
        title={disabledReason ?? (ledgerId ? undefined : "This idea has no ledger reference yet.")}
        onClick={copyPrompt}
      >
        {status === "copied" ? (
          <>
            <Check className="size-3.5 text-gain" />
            Copied!
          </>
        ) : status === "failed" ? (
          <>
            <AlertTriangle className="size-3.5 text-warning" />
            Copy failed
          </>
        ) : (
          <>
            <Copy className="size-3.5" />
            Ask Claude
          </>
        )}
      </Button>
      {status === "failed" && fallbackPrompt != null && (
        <div role="alert" className="mt-1.5 space-y-1">
          <p className="text-[11px] text-warning">Copy failed — copy manually below.</p>
          <textarea
            ref={fallbackRef}
            readOnly
            value={fallbackPrompt}
            aria-label="Ask Claude prompt (copy manually)"
            className="w-full resize-none rounded border border-warning/30 bg-background p-1.5 font-mono text-[11px] text-foreground"
            rows={3}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      )}
    </div>
  );
}
